import { useEffect, useMemo, useState } from 'react';
import { UnleashClient } from 'unleash-proxy-client';
import { getUnleashClient } from '../services/unleash-feature-flags.service';
import ApiStore from '../store/ApiStore';
import { useStore } from './use-store';

const UNLEASH_CLIENT_CHECK_READY_INTERVAL_MS = 100;

export const useDirectFeatureFlag = (flagName: string): boolean | undefined => {
  const [unleashClient, setUnleashClient] = useState<UnleashClient | null>();
  const [isFeatureEnabled, setIsFeatureEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const interval = setInterval(() => {
      const client = getUnleashClient();
      if (client?.isReady()) {
        setUnleashClient(client);
        clearInterval(interval);
      }
    }, UNLEASH_CLIENT_CHECK_READY_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (unleashClient) {
      const flagChangeHandler = (): void => setIsFeatureEnabled(unleashClient.isEnabled(flagName));
      flagChangeHandler();
      unleashClient.on('update', flagChangeHandler);
      return () => {
        unleashClient.off('update', flagChangeHandler);
      };
    }
  }, [flagName, unleashClient]);

  return isFeatureEnabled;
};

export const getUserFeatureFlag = (apiStore: ApiStore, featureFlagName: string): boolean =>
  apiStore.user?.featureFlags?.[featureFlagName] || false;

export const useUserFeatureFlag = (featureFlagName: string): boolean => {
  const store = useStore().apiStore;
  return useMemo(() => getUserFeatureFlag(store, featureFlagName), [featureFlagName, store]);
};
